/**
 * 작성자 : 한영광
 * 날짜 : 2023.06.26
 * 경로 : 출역 관리 - 월 출역현황
 */
import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../../../translation/i18n';
import styled from 'styled-components';

import { userState } from '../../../atoms';
import { BtnGhost } from '../../../components/Button';
import { MONTH_LIST, PMS_NORMAL_SUB_CODE, YEAR_LIST } from '../../../_constants';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { scrollToNodeTop } from '../../../utils/scrollToNodeTop';
import { useDetectScrolledToBottom } from '../../../utils/useDetectScrolledToBottom';
import { FloatingButtonToTop } from '../../../assets/styles/FloatingButtonToTop';
import { pad, todayYYYYMMDD, getFirstDayOfMonth, getLastDayOfMonth } from '../../../utils/formatDate';
import Portal from '../../../components/Portal';
import AttendListModal from '../../../components/Modal/AttendListModal';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import { logPost } from '../../../services/log';
import SearchSelectBoxSm from '../../../components/SearchSelectBoxSm';
import { useSetAuth } from '../../../utils/useSetAuth';
import TuiGrid from '../../../components/Table/TuiGrid';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { apiGet } from '../../../services/_common';
import { isMobile } from '../../../utils/checkMobile';
import SelectBox from '../../../components/SelectBox';
import ShortcutButton from '../../../components/button/ShortcutButton';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import { ISelectBox } from 'customTypes';
import { showExcelButton } from '../../../utils/deviceAuth';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;

  > :nth-child(1) {
    align-self: flex-start;
  }
`;

interface IModal {
  status: boolean;
  type: string;
  searchOption: ISearchOption;
}

interface ISearchOption {
  sjName: string;
  sjCd: string;
  startDate: string;
  endDate: string;
  partName?: string;
  partCd?: string;
}

const Main = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const location = useLocation();
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue(userState);
  const [columns, setColumns] = useState<any[]>([]);
  const [summary, setSummary] = useState({});
  const tableRef = useRef<HTMLDivElement>(null);
  const [orgSiteJoinInfoList, setOrgSiteJoinInfoList] = useState<any[]>([]);

  const [partCd, setPartCd] = useState({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });

  const [partList, setPartList] = useState<ISelectBox[]>([]);

  const [aYear, setAYear] = useState({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
  const [month, setMonth] = useState({ type: 'month', month: toDay.substring(4, 6), cdName: toDay.substring(4, 6) });
  const [excelBtn, setExcelBtn] = useState(false);
  const [tableState, setTableState] = useState<any[]>([]);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', searchOption: { partName: 'faf', sjName: '', sjCd: '', startDate: '', endDate: '' } });
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    const days = new Date(Number(aYear.aYear), Number(month.month), 0).getDate();
    const header = [];
    header.push({ header: t('부서명'), name: 'partName', align: 'left', filter: 'select', width: 240, renderer: { classNames: ['text_secondary'] } });
    if (isMobile()) {
      header.push({ header: t('총합'), name: 'total', align: 'center', minWidth: 60 });
    }
    for (let i = 0; i < days; i += 1) {
      header.push({
        header: `${i + 1}${t('-일')}`,
        name: `${aYear.aYear}${month.month}${pad(i + 1, 2)}`,
        align: 'center',
        minWidth: 60,
        renderer: { classNames: ['text_secondary'] },
      });
    }
    if (!isMobile()) {
      header.push({ header: t('총합'), name: 'total', align: 'center', minWidth: 60 });
    }
    setColumns(header);

    const summaryArr = [];
    for (let i = 0; i < days; i += 1) {
      summaryArr.push({
        header: `${i + 1}${t('-일')}`,
        name: `${aYear.aYear}${month.month}${pad(i + 1, 2)}`,
        align: 'center',
      });
    }
    const summaryObj: any = {};
    summaryArr.map((v: any) => {
      summaryObj[v.name] = {
        template: (valueMap: any) => {
          return `${valueMap.sum}`;
        },
      };
    });
    setSummary({
      height: 30,
      position: 'top',
      columnContent: {
        partName: t('전체'),
        ...summaryObj,
        total: {
          template: (valueMap: any) => {
            return `${valueMap.sum}`;
          },
        },
      },
    });
  }, [tableState, i18n.language]);

  // 월 출역현황 조회 API
  const getAttendMonthAPI = async () => {
    const newSearchOption = { partCd: partCd.PART_CD === 'A' ? '' : partCd.PART_CD, aMonth: `${aYear.aYear}${month.month}` };
    const req = { ...newSearchOption, cCd: userInfo.hCd };
    const res = await apiGet({ path: '/attend/month/company', req });

    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      setTableState(
        data.attendList.map((v: any) => {
          let total = 0;
          Object.entries(v.day).map(([key, val]: any) => {
            total += Number(val);
          });
          return {
            partName: v.partName,
            ...v.day,
            total,
          };
        })
      );
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
  };

  // 부서명, 직급명 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const defaultObject = { type: cdFind, [cdFind]: 'A', cdName: t('전체') };
      const newArr = res.data.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([defaultObject, ...newArr]);
    }
  };

  useEffect(() => {
    setPmsNormalSubList('L', setPartList);
  }, []);

  // 검색 버튼 클릭
  const onClickSearch = () => {
    if (!openModal.status) {
      setLoading(true);
      navigate({
        pathname: location.pathname,
        search: '',
      });
      getAttendMonthAPI();
    }
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '월 출역현황',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  const isTotalClickAble = (rowData: any, columnName: string) => {
    return columnName === 'partName' && rowData === 'summary';
  };

  const isClickAble = (rowData: any, columnName: string) => {
    return columnName !== 'partName' && columnName !== 'total' && columnName !== '_number' && rowData && rowData[columnName] !== 0;
  };

  const getSearchOptionOfMonth = () => {
    const searchYear = Object.keys(tableState[0])[0].slice(0, 4);
    const searchMonth = Object.keys(tableState[0])[0].slice(4, 6);
    return {
      partName: '전체',
      partCd: '',
      sjName: '',
      sjCd: '',
      startDate: getFirstDayOfMonth(Number(searchYear), Number(searchMonth)),
      endDate: getLastDayOfMonth(Number(searchYear), Number(searchMonth)),
    };
  };

  const getSearchOptionOfDay = (rowData: any, columnName: string) => {
    const partObj = partList.find((partItem) => partItem.cdName === rowData.partName);
    return {
      partName: partObj?.cdName,
      partCd: partObj ? partObj?.PART_CD : '',
      sjName: '',
      sjCd: '',
      startDate: `${columnName.substring(0, 4)}-${pad(Number(columnName.substring(4, 6)), 2)}-${pad(Number(columnName.substring(6, 8)), 2)}`,
      endDate: `${columnName.substring(0, 4)}-${pad(Number(columnName.substring(4, 6)), 2)}-${pad(Number(columnName.substring(6, 8)), 2)}`,
    };
  };

  // Row 클릭
  const onClickRow = (rowData: any, columnName: string) => {
    if (isTotalClickAble(rowData, columnName)) {
      const searchOption = getSearchOptionOfMonth();
      setOpenModal({ status: true, type: 'dailyAttendList', searchOption });
    }
    if (isClickAble(rowData, columnName)) {
      const searchOption = getSearchOptionOfDay(rowData, columnName);
      setOpenModal({ status: true, type: 'dailyAttendList', searchOption });
    }
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span>
          {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  return (
    <>
      <Root loading={loading}>
        <SearchOptions>
          {isBottom && (
            <FloatingButtonToTop>
              <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
              </button>
            </FloatingButtonToTop>
          )}
          <div className='inputsWrapper'>
            <div className='inputForm-row withLabel'>
              <label htmlFor='partCd'>{t('부서명')}</label>
              <div className='inputForm-col'>
                <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
                {/* <SearchSelectBoxSm
                  options={siteJoinInfoList}
                  defaultOption={location.state?.sjName || t('전체')}
                  state={sjCd}
                  setState={setSjCd}
                  stateKey='sjCd'
                  codeKey='cdName'
                  filterbar='filter-1-left'
                  optionHeight='height-md'
                /> */}
              </div>
            </div>
            <div className='inputForm-row'>
              <div className='inputForm-col'>
                <SelectBox options={YEAR_LIST} defaultOption={aYear.aYear} state={aYear} setState={setAYear} stateKey='aYear' filterbar='filter-1-left' optionHeight='height-md' />
              </div>
            </div>
            <div className='inputForm-row'>
              <div className='inputForm-col'>
                <SelectBox options={MONTH_LIST} defaultOption={month.month} state={month} setState={setMonth} stateKey='month' filterbar='filter-1-left' optionHeight='height-sm' />
              </div>
            </div>
          </div>
          <div className='inputsWrapper'>
            <div className='secondSearchOption'>
              <div className='flex-basic textBtnGroup'>
                <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
              </div>
              <div className='flex-basic iconBtnGroup'>
                {renderExcelBtn()}
                {/* {renderPrintBtn()} */}
              </div>
            </div>
          </div>
        </SearchOptions>
        <TuiGridWrapper componentRef={componentRef}>
          <TuiGrid
            data={tableState}
            columns={columns}
            perPage={5}
            excelBtn={excelBtn}
            setExcelBtn={setExcelBtn}
            onClickRow={onClickRow}
            frozenCount={frozenCount}
            summary={summary}
            height={tuiHeight}
            scrollX
          />
        </TuiGridWrapper>
        {loading && (
          <LoadingModalBackground>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
          </LoadingModalBackground>
        )}
      </Root>
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'dailyAttendList' && <AttendListModal setOpenModal={setOpenModal} filter={openModal.searchOption} dGubun='c' />}
      </Portal>
    </>
  );
};

export default Main;
